/**
 * Producto — módulo nuevo (feedback Compras/Ventas).
 *
 * Permite parametrizar:
 *   · Variedades: catálogo (FIXED_VARIETIES + custom). Las custom se
 *     persisten en data.customVarieties[] y aparecen automáticamente en
 *     todos los dropdowns (ventas, compras, devoluciones, inventario).
 *   · Combos: catálogo de combos preestablecidos. PREDEFINED_COMBOS es
 *     read-only (definido en el paquete shared) y los combos custom se
 *     gestionan acá con CRUD completo. La pantalla de Nueva Venta los
 *     consume desde GET /api/combos (unión).
 */
import { useMemo, useState } from 'react'
import {
  Leaf,
  Package,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { BOX_SIZES } from '@expogreens/shared'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar } from '@/components/ui/FilterBar'
import { ChoiceButtons } from '@/components/ui/ChoiceButtons'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

const EMPTY_COMBO_ITEM = { variety: '', quantity: 0, stemLength: 60 }
const EMPTY_BOX = () => ({ boxSize: 'fullbox', boxCount: 1, items: [] })

const ICON_BTN_OUTLINE = {
  width: 34,
  height: 34,
  borderRadius: 10,
  border: '1px solid #E6EFE3',
  background: '#fff',
  color: '#5C6B60',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flex: 'none',
}
const ICON_BTN_DANGER = { ...ICON_BTN_OUTLINE, color: '#C0392B', borderColor: '#F3D6D2' }

/**
 * Tarjeta de combo (handoff Producto): nombre en display, descripción, items
 * con cantidad en verde + longitud a la derecha, y chips de empaque. Si recibe
 * onEdit/onDelete, muestra las acciones (combos personalizados); los
 * preestablecidos van sin acciones (read-only).
 */
function ComboCard({ combo, onEdit, onDelete }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #EEF2EC', borderRadius: 18, padding: '20px 22px' }}>
      <div className="flex items-start justify-between" style={{ gap: 12, marginBottom: 12 }}>
        <div className="min-w-0">
          <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#143A22', lineHeight: 1.1 }}>
            {combo.name}
          </div>
          {combo.description && (
            <div style={{ fontSize: 14, color: '#8B9B8F', fontWeight: 500, marginTop: 3 }}>
              {combo.description}
            </div>
          )}
        </div>
        {(onEdit || onDelete) && (
          <div className="flex items-center" style={{ gap: 8, flex: 'none' }}>
            {onEdit && (
              <button type="button" onClick={onEdit} title="Editar" style={ICON_BTN_OUTLINE}>
                <Pencil size={16} />
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={onDelete} title="Eliminar" style={ICON_BTN_DANGER}>
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col">
        {(combo.items || []).map((it, i) => (
          <div
            key={i}
            className="flex items-center justify-between"
            style={{ padding: '8px 0', borderTop: i > 0 ? '1px solid #F4F7F1' : 'none' }}
          >
            <div className="flex items-baseline" style={{ gap: 14 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#1E6E3A', minWidth: 44 }}>
                {it.quantity}
              </span>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#143A22' }}>{it.variety}</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#9BA89B' }}>{it.stemLength}cm</span>
          </div>
        ))}
      </div>

      {Array.isArray(combo.defaultPacking) && combo.defaultPacking.length > 0 && (
        <div className="flex flex-wrap" style={{ gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid #EEF2EC' }}>
          {combo.defaultPacking.map((b, i) => (
            <span
              key={i}
              className="inline-flex items-center"
              style={{ gap: 7, background: '#E3F0D5', color: '#1E6E3A', fontSize: 13, fontWeight: 700, padding: '6px 12px', borderRadius: 999 }}
              title={(b.items || []).map((it) => `${it.quantity} ${it.variety} ${it.stemLength}cm`).join(' · ')}
            >
              <Package size={14} />
              {BOX_SIZES[b.boxSize] || b.boxSize} × {b.boxCount}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ProductsPage() {
  const varieties = useApi('/varieties')
  const combos = useApi('/combos')
  const inventory = useApi('/inventory')

  // Stock total por variedad — una variedad con unidades en inventario NO se
  // puede eliminar.
  const stockByVariety = useMemo(() => {
    const m = new Map()
    for (const r of inventory.data || []) {
      const total =
        (Number(r.stages?.unprocessed?.total) || 0) +
        (Number(r.stages?.in_process?.total) || 0) +
        (Number(r.stages?.cold_room?.total) || 0)
      if (!r.variety) continue
      m.set(r.variety, (m.get(r.variety) || 0) + total)
    }
    return m
  }, [inventory.data])

  const [newVariety, setNewVariety] = useState('')
  const [creatingVariety, setCreatingVariety] = useState(false)
  // Filtro de combos: todos | preestablecidos | personalizados.
  const [comboFilter, setComboFilter] = useState('all')
  const [editingCombo, setEditingCombo] = useState(null) // null | {} | combo
  const [comboName, setComboName] = useState('')
  const [comboDesc, setComboDesc] = useState('')
  const [comboItems, setComboItems] = useState([{ ...EMPTY_COMBO_ITEM }])
  // Empaque por defecto del combo — LISTA de cajas. Cada caja define
  // su tipo, cantidad y los items (variedad/longitud/cantidad) que van
  // dentro. Al usar el combo en una venta, esta lista se aplica en el
  // Step 3 multiplicando boxCount × multiplier del combo.
  const [comboPacking, setComboPacking] = useState([])

  const addVariety = useMutation((name) =>
    apiCall('/varieties', { method: 'POST', body: { name } }),
  )
  const delVariety = useMutation((name) =>
    apiCall(`/varieties/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  )
  const createCombo = useMutation((payload) =>
    apiCall('/combos', { method: 'POST', body: payload }),
  )
  const updateCombo = useMutation(({ id, payload }) =>
    apiCall(`/combos/${id}`, { method: 'PUT', body: payload }),
  )
  const deleteCombo = useMutation((id) =>
    apiCall(`/combos/${id}`, { method: 'DELETE' }),
  )

  // Combos preestablecidos vs custom. La heurística: los que vienen de
  // PREDEFINED_COMBOS tienen id con prefijo "COMBO-" (sin C) y NO tienen
  // isCustom; los custom traen isCustom=true.
  const { fixedCombos, customCombos } = useMemo(() => {
    const list = combos.data || []
    return {
      fixedCombos: list.filter((c) => !c.isCustom),
      customCombos: list.filter((c) => c.isCustom),
    }
  }, [combos.data])

  const openNewCombo = () => {
    setEditingCombo({})
    setComboName('')
    setComboDesc('')
    setComboItems([{ ...EMPTY_COMBO_ITEM }])
    setComboPacking([])
  }
  const openEditCombo = (c) => {
    setEditingCombo(c)
    setComboName(c.name || '')
    setComboDesc(c.description || '')
    setComboItems(
      (c.items || []).map((it) => ({
        variety: it.variety || '',
        quantity: Number(it.quantity) || 0,
        stemLength: Number(it.stemLength) || 60,
      })),
    )
    // Hidratar el empaque por defecto soportando dos formatos:
    //   - array de cajas (formato actual)
    //   - objeto único {boxSize, boxCount} (legacy) → 1 caja con TODOS
    //     los items del combo.
    const dp = c.defaultPacking
    if (Array.isArray(dp)) {
      setComboPacking(
        dp.map((b) => ({
          boxSize: b.boxSize || 'fullbox',
          boxCount: Number(b.boxCount) || 1,
          items: (b.items || []).map((it) => ({
            variety: it.variety || '',
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
        })),
      )
    } else if (dp && typeof dp === 'object') {
      setComboPacking([
        {
          boxSize: dp.boxSize || 'fullbox',
          boxCount: Number(dp.boxCount) || 1,
          items: (c.items || []).map((it) => ({
            variety: it.variety || '',
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
        },
      ])
    } else {
      setComboPacking([])
    }
  }
  const closeComboModal = () => {
    setEditingCombo(null)
    setComboName('')
    setComboDesc('')
    setComboItems([{ ...EMPTY_COMBO_ITEM }])
    setComboPacking([])
  }

  // ── Handlers del editor de cajas por defecto ──────────────────────
  const addComboBox = () => setComboPacking((l) => [...l, EMPTY_BOX()])
  const removeComboBox = (i) =>
    setComboPacking((l) => l.filter((_, idx) => idx !== i))
  const patchComboBox = (i, patch) =>
    setComboPacking((l) => l.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))
  const addBoxItem = (boxIdx) =>
    setComboPacking((l) =>
      l.map((b, i) =>
        i === boxIdx
          ? { ...b, items: [...(b.items || []), { variety: '', stemLength: 60, quantity: 0 }] }
          : b,
      ),
    )
  const removeBoxItem = (boxIdx, itemIdx) =>
    setComboPacking((l) =>
      l.map((b, i) =>
        i === boxIdx
          ? { ...b, items: (b.items || []).filter((_, j) => j !== itemIdx) }
          : b,
      ),
    )
  const patchBoxItem = (boxIdx, itemIdx, patch) =>
    setComboPacking((l) =>
      l.map((b, i) =>
        i === boxIdx
          ? {
              ...b,
              items: (b.items || []).map((it, j) =>
                j === itemIdx ? { ...it, ...patch } : it,
              ),
            }
          : b,
      ),
    )

  // Prellena las cajas con los items totales del combo (1 sola caja con
  // todo adentro). Atajo cuando empaque y contenido coinciden.
  const presetFromComboItems = () => {
    setComboPacking([
      {
        boxSize: 'fullbox',
        boxCount: 1,
        items: comboItems
          .filter((it) => it.variety && Number(it.quantity) > 0)
          .map((it) => ({
            variety: it.variety,
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
      },
    ])
  }

  const onAddVariety = async (e) => {
    e.preventDefault()
    if (!newVariety.trim()) return
    try {
      await addVariety.mutate(newVariety.trim())
      toast.success(`Variedad ${newVariety} creada`)
      setNewVariety('')
      varieties.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelVariety = async (name) => {
    const stock = stockByVariety.get(name) || 0
    if (stock > 0) {
      return toast.error(
        `No se puede eliminar ${name}: hay ${stock.toLocaleString('es-CO')} en inventario.`,
      )
    }
    if (!confirm(`¿Eliminar la variedad ${name}? (solo afecta a variedades creadas en este módulo)`)) return
    try {
      await delVariety.mutate(name)
      toast.success(`Variedad ${name} eliminada`)
      varieties.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onSubmitCombo = async (e) => {
    e.preventDefault()
    if (!comboName.trim()) return toast.error('Nombre del combo requerido')
    const cleaned = comboItems
      .filter((it) => it.variety && Number(it.quantity) > 0)
      .map((it) => ({
        variety: it.variety,
        quantity: Number(it.quantity),
        stemLength: Number(it.stemLength) || 60,
      }))
    if (cleaned.length === 0) return toast.error('Agrega al menos un item al combo')
    // defaultPacking: array de cajas, cada una con sus items. El
    // backend lo normaliza (filtra cajas vacías, valida boxSize, etc.).
    const cleanedPacking = comboPacking
      .map((b) => ({
        boxSize: b.boxSize || 'fullbox',
        boxCount: Math.max(1, Number(b.boxCount) || 1),
        items: (b.items || [])
          .filter((it) => it.variety && Number(it.quantity) > 0)
          .map((it) => ({
            variety: it.variety,
            stemLength: Number(it.stemLength) || 0,
            quantity: Number(it.quantity) || 0,
          })),
      }))
      .filter((b) => b.items.length > 0)
    const payload = {
      name: comboName,
      description: comboDesc,
      items: cleaned,
      defaultPacking: cleanedPacking,
    }
    try {
      if (editingCombo?.id) {
        await updateCombo.mutate({ id: editingCombo.id, payload })
        toast.success(`Combo ${editingCombo.id} actualizado`)
      } else {
        await createCombo.mutate(payload)
        toast.success('Combo creado')
      }
      closeComboModal()
      combos.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelCombo = async (c) => {
    if (!confirm(`¿Eliminar combo ${c.name}?`)) return
    try {
      await deleteCombo.mutate(c.id)
      toast.success('Combo eliminado')
      combos.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const patchComboItem = (i, patch) =>
    setComboItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  const addComboItem = () => setComboItems((l) => [...l, { ...EMPTY_COMBO_ITEM }])
  const removeComboItem = (i) =>
    setComboItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))

  return (
    <>
      <ModuleShell
        title="Producto"
        subtitle="Catálogo de variedades y combos parametrizables"
        icon={Leaf}
        action={
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={openNewCombo} className="!px-3 sm:!px-6">
              <Plus size={18} />
              <span className="hidden sm:inline">Nuevo combo</span>
            </Button>
            <Button onClick={() => { setNewVariety(''); setCreatingVariety(true) }} className="!px-3 sm:!px-6">
              <Plus size={18} />
              <span className="hidden sm:inline">Nueva variedad</span>
            </Button>
          </div>
        }
        filters={
          <FilterBar left={
            <Segmented
              value={comboFilter}
              onChange={setComboFilter}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'fixed', label: 'Preestablecidos' },
                { value: 'custom', label: 'Personalizados' },
              ]}
            />
          } />
        }
      >
      <div className="space-y-6">
      {/* ── Variedades ─────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 22, padding: '26px 28px' }}>
        <div className="flex items-baseline" style={{ gap: 12, marginBottom: 20 }}>
          <h3 className="font-display" style={{ fontSize: 28, fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em' }}>
            Variedades
          </h3>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#9BA89B' }}>
            {(varieties.data || []).length}
          </span>
        </div>

        {varieties.data && (
          <div className="flex flex-wrap" style={{ gap: 12 }}>
            {varieties.data.length === 0 && (
              <span style={{ fontSize: 14, color: '#8B9B8F' }}>Sin variedades todavía.</span>
            )}
            {varieties.data.map((v) => {
              const stock = stockByVariety.get(v) || 0
              const locked = stock > 0
              return (
                <span
                  key={v}
                  className="inline-flex items-center"
                  style={{ gap: 9, background: '#F4F9EC', border: '1px solid #E0EBC9', borderRadius: 999, padding: '9px 14px' }}
                >
                  <Leaf size={16} style={{ color: '#1E6E3A', flex: 'none' }} />
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#143A22' }}>{v}</span>
                  <button
                    type="button"
                    onClick={() => onDelVariety(v)}
                    disabled={locked}
                    title={
                      locked
                        ? `No se puede eliminar: ${stock.toLocaleString('es-CO')} en inventario`
                        : 'Eliminar (solo si es custom)'
                    }
                    className={locked ? '' : 'press'}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: locked ? 'not-allowed' : 'pointer',
                      color: locked ? '#C9D3C9' : '#9BA89B',
                      display: 'inline-flex',
                      padding: 0,
                    }}
                    onMouseEnter={(e) => { if (!locked) e.currentTarget.style.color = '#C0392B' }}
                    onMouseLeave={(e) => { if (!locked) e.currentTarget.style.color = '#9BA89B' }}
                  >
                    <X size={16} />
                  </button>
                </span>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Combos ─────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 22, padding: '26px 28px' }}>
        <h3 className="font-display" style={{ fontSize: 28, fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em', marginBottom: 22 }}>
          Combos
        </h3>

        {/* Preestablecidos (catálogo shared, read-only) */}
        {comboFilter !== 'custom' && fixedCombos.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A9B4AA', marginBottom: 14 }}>
              Preestablecidos
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ marginBottom: 28 }}>
              {fixedCombos.map((c) => (
                <ComboCard key={c.id} combo={c} />
              ))}
            </div>
          </>
        )}

        {/* Custom (CRUD) */}
        {comboFilter !== 'fixed' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A9B4AA', marginBottom: 14 }}>
              Combos personalizados
            </div>
            {customCombos.length === 0 ? (
              <p style={{ fontSize: 14, color: '#8B9B8F', fontStyle: 'italic' }}>
                Sin combos personalizados. Crea uno con "Nuevo combo".
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customCombos.map((c) => (
                  <ComboCard
                    key={c.id}
                    combo={c}
                    onEdit={() => openEditCombo(c)}
                    onDelete={() => onDelCombo(c)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      </div>
      </ModuleShell>

      {/* ── Modal: Nueva variedad ──────────────────────────────────── */}
      <Modal
        open={creatingVariety}
        onClose={() => setCreatingVariety(false)}
        title="Nueva variedad"
        subtitle="Aparecerá en ventas, compras, inventario y devoluciones"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            const name = newVariety.trim()
            if (!name) return
            try {
              await addVariety.mutate(name)
              toast.success(`Variedad ${name} creada`)
              setNewVariety('')
              setCreatingVariety(false)
              varieties.refresh()
            } catch (err) {
              toast.error(err.message)
            }
          }}
          className="space-y-4"
        >
          <Field label="Nombre de la variedad" required>
            <Input
              value={newVariety}
              onChange={(e) => setNewVariety(e.target.value)}
              placeholder="p. ej. Eucalipto"
              autoFocus
              required
            />
          </Field>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setCreatingVariety(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={addVariety.loading || !newVariety.trim()}>
              {addVariety.loading ? 'Creando…' : 'Crear variedad'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(editingCombo)}
        onClose={closeComboModal}
        title={editingCombo?.id ? `Editar combo ${editingCombo.id}` : 'Nuevo combo'}
        subtitle="Variedades, longitudes y empaque por defecto"
        size="lg"
      >
        <form onSubmit={onSubmitCombo} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre" required>
              <Input
                value={comboName}
                onChange={(e) => setComboName(e.target.value)}
                required
              />
            </Field>
            <Field label="Descripción">
              <Input
                value={comboDesc}
                onChange={(e) => setComboDesc(e.target.value)}
              />
            </Field>
          </div>

          {/*
            Empaque por defecto del combo — LISTA de cajas. Cada caja
            tiene su tipo, cantidad y los items (variedad/longitud/
            cantidad) que van adentro. Un mismo combo puede empacarse
            en varias cajas (ej. una Full Box con verdes + una Half
            Box con tropicales). Editable en la venta antes de
            despachar.
          */}
          <div className="rounded-xl border border-expo-primary/30 bg-expo-light/30 p-3 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Package size={16} className="text-expo-dark" />
              <span className="text-sm font-semibold text-expo-dark">
                Cajas por defecto
              </span>
              <span className="text-xs font-normal text-gray-500">
                (se aplican al usar el combo en una venta, editable después)
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={presetFromComboItems}
                  className="text-xs text-expo-primary hover:text-expo-dark font-medium"
                  title="Atajo: 1 caja con todos los items del combo"
                >
                  Auto: todo en una caja
                </button>
                <button
                  type="button"
                  onClick={addComboBox}
                  className="text-xs text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
                >
                  <Plus size={14} />
                  Añadir caja
                </button>
              </div>
            </div>

            {comboPacking.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                Sin cajas configuradas. Sin empaque por defecto, el operario
                tendrá que armar las cajas a mano en cada venta.
              </p>
            ) : (
              <div className="space-y-3">
                {comboPacking.map((box, bi) => (
                  <div
                    key={bi}
                    className="rounded-xl border border-expo-line bg-white p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Caja #{bi + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeComboBox(bi)}
                        className="ml-auto p-1 text-gray-400 hover:text-red-600"
                        title="Eliminar esta caja"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <Field label="Tipo de caja">
                      <ChoiceButtons
                        value={box.boxSize}
                        onChange={(v) => patchComboBox(bi, { boxSize: v })}
                        options={Object.entries(BOX_SIZES).map(([id, label]) => ({ value: id, label }))}
                      />
                    </Field>
                    <Field
                      label="Cajas iguales"
                      hint="Por unidad del combo (el multiplicador de la venta vuelve a multiplicar)."
                    >
                      <Input
                        type="number"
                        min="1"
                        value={box.boxCount}
                        onChange={(e) =>
                          patchComboBox(bi, { boxCount: e.target.value })
                        }
                      />
                    </Field>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-expo-dark">
                          Contenido de cada caja
                        </span>
                        <button
                          type="button"
                          onClick={() => addBoxItem(bi)}
                          className="text-xs text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
                        >
                          <Plus size={12} />
                          Añadir variedad
                        </button>
                      </div>
                      <div className="space-y-1">
                        <div className="grid grid-cols-12 gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          <div className="col-span-6">Variedad</div>
                          <div className="col-span-2">cm</div>
                          <div className="col-span-3">Cantidad</div>
                          <div className="col-span-1" />
                        </div>
                        {(box.items || []).length === 0 && (
                          <p className="text-xs text-gray-400 italic px-2">
                            Caja vacía — agrega al menos una variedad.
                          </p>
                        )}
                        {(box.items || []).map((it, ii) => (
                          <div
                            key={ii}
                            className="grid grid-cols-12 gap-2 bg-expo-light/40 p-2 rounded-lg items-center"
                          >
                            <div className="col-span-6">
                              <Select
                                value={it.variety}
                                onChange={(e) =>
                                  patchBoxItem(bi, ii, { variety: e.target.value })
                                }
                              >
                                <option value="">Variedad…</option>
                                {(varieties.data || []).map((v) => (
                                  <option key={v} value={v}>
                                    {v}
                                  </option>
                                ))}
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                min="0"
                                value={it.stemLength}
                                onChange={(e) =>
                                  patchBoxItem(bi, ii, { stemLength: e.target.value })
                                }
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                min="0"
                                value={it.quantity}
                                onChange={(e) =>
                                  patchBoxItem(bi, ii, { quantity: e.target.value })
                                }
                              />
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeBoxItem(bi, ii)}
                                className="p-1 text-gray-400 hover:text-red-600"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-expo-dark">Items</span>
              <button
                type="button"
                onClick={addComboItem}
                className="text-sm text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
              >
                <Plus size={16} />
                Añadir item
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-5">Variedad</div>
                <div className="col-span-3">Cantidad</div>
                <div className="col-span-3">Longitud (cm)</div>
                <div className="col-span-1" />
              </div>
              {comboItems.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 bg-expo-light/50 p-3 rounded-xl items-center"
                >
                  <div className="col-span-5">
                    <Select
                      value={it.variety}
                      onChange={(e) => patchComboItem(idx, { variety: e.target.value })}
                      required
                    >
                      <option value="">Variedad…</option>
                      {(varieties.data || []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      value={it.quantity}
                      onChange={(e) => patchComboItem(idx, { quantity: e.target.value })}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      value={it.stemLength}
                      onChange={(e) => patchComboItem(idx, { stemLength: e.target.value })}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeComboItem(idx)}
                      disabled={comboItems.length === 1}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={closeComboModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createCombo.loading || updateCombo.loading}
            >
              {(createCombo.loading || updateCombo.loading)
                ? 'Guardando…'
                : editingCombo?.id
                  ? 'Guardar cambios'
                  : 'Crear combo'}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  )
}
